/**
 * Organization, User, and WidgetInstance admin DTOs.
 *
 * Organizations + users are managed by the meta-admin (platform operator);
 * widget instances are managed by the org-admin within their own org. The
 * WidgetInstance config editor in the dashboard reads/writes these shapes; the
 * browser-safe projection lives in `widget-config.ts` (WidgetConfig).
 */
import type { z } from 'zod';
import { array, email, iso, object, string } from 'zod';

import {
  CaptureLevelSchema,
  OrgPlanSchema,
  OrgStatusSchema,
  RoleSchema,
  WidgetInstanceStatusSchema,
} from './enums.js';
import { AllowedOriginsSchema } from './origin.js';
import {
  AgentConfigSchema,
  BrandingConfigSchema,
  FeatureFlagsSchema,
  MaskingConfigSchema,
} from './widget-config.js';

// ─────────────────────────────────────────────────────────────────────────────
// Organization
// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationSchema = object({
  id: string().min(1),
  name: string().min(1).max(256),
  plan: OrgPlanSchema,
  status: OrgStatusSchema,
  createdAt: iso.datetime(),
});
export type Organization = z.infer<typeof OrganizationSchema>;

/** Meta-admin: create a new organization. */
export const CreateOrganizationSchema = object({
  name: string().min(1).max(256),
  plan: OrgPlanSchema.default('free'),
});
export type CreateOrganization = z.infer<typeof CreateOrganizationSchema>;

/** Meta-admin: update org (suspend / change plan / rename). */
export const UpdateOrganizationSchema = object({
    name: string().min(1).max(256).optional(),
    plan: OrgPlanSchema.optional(),
    status: OrgStatusSchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
  });
export type UpdateOrganization = z.infer<typeof UpdateOrganizationSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// User
// ─────────────────────────────────────────────────────────────────────────────

/** Safe user projection (never includes passwordHash). */
export const UserSchema = object({
  id: string().min(1),
  email: email(),
  name: string().nullable().optional(),
  role: RoleSchema,
  organizationId: string().nullable().optional(),
  /** Set only for PROJECT_ADMIN — the single project the user is attached to. */
  widgetInstanceId: string().nullable().optional(),
  externalId: string().nullable().optional(),
  createdAt: iso.datetime(),
});
export type User = z.infer<typeof UserSchema>;

/** Invite a new user (org-admin invites into their org; meta-admin can target any org/role). */
export const InviteUserSchema = object({
  email: email(),
  name: string().max(256).optional(),
  role: RoleSchema.default('ORG_ADMIN'),
  /** Required for ORG_ADMIN / PROJECT_ADMIN / REPORTER; forbidden for META_ADMIN. */
  organizationId: string().min(1).optional(),
  /** Required for PROJECT_ADMIN; forbidden for every other role. */
  widgetInstanceId: string().min(1).optional(),
});
export type InviteUser = z.infer<typeof InviteUserSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// WidgetInstance (admin config editor)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Full admin view of a widget instance. `secretHash` is never returned; the
 * plaintext secret is surfaced once at creation via `WidgetInstanceSecretSchema`.
 */
export const WidgetInstanceSchema = object({
  id: string().min(1),
  organizationId: string().min(1),
  name: string().min(1).max(256),
  publicKey: string().min(1),
  captureLevel: CaptureLevelSchema,
  /**
   * Deliberately NOT `AllowedOriginsSchema` — this is the RESPONSE schema, and
   * the serializer parses every row through it. Validating here would turn a
   * pre-existing malformed entry into a 500 on read, so the operator could no
   * longer open the editor to fix the very value that is breaking it. The write
   * schemas below validate; the dashboard flags bad stored entries in the UI.
   */
  allowedOrigins: array(string().min(1).max(2048)).max(100),
  masking: MaskingConfigSchema,
  features: FeatureFlagsSchema,
  agent: AgentConfigSchema,
  branding: BrandingConfigSchema,
  status: WidgetInstanceStatusSchema,
  createdAt: iso.datetime(),
  updatedAt: iso.datetime(),
});
export type WidgetInstance = z.infer<typeof WidgetInstanceSchema>;

/** Create a widget instance (org-scoped). All config sub-objects are optional with sane defaults. */
export const CreateWidgetInstanceSchema = object({
  name: string().min(1).max(256),
  captureLevel: CaptureLevelSchema.default(1),
  allowedOrigins: AllowedOriginsSchema.default([]),
  masking: MaskingConfigSchema.optional(),
  features: FeatureFlagsSchema.optional(),
  agent: AgentConfigSchema.optional(),
  branding: BrandingConfigSchema.optional(),
});
export type CreateWidgetInstance = z.infer<typeof CreateWidgetInstanceSchema>;

/** Patch a widget instance (config editor save). */
export const UpdateWidgetInstanceSchema = object({
  name: string().min(1).max(256).optional(),
  captureLevel: CaptureLevelSchema.optional(),
  allowedOrigins: AllowedOriginsSchema.optional(),
  masking: MaskingConfigSchema.optional(),
  features: FeatureFlagsSchema.optional(),
  agent: AgentConfigSchema.optional(),
  branding: BrandingConfigSchema.optional(),
  status: WidgetInstanceStatusSchema.optional(),
});
export type UpdateWidgetInstance = z.infer<typeof UpdateWidgetInstanceSchema>;

/**
 * Returned ONCE when a widget instance is created (or its secret rotated): the
 * plaintext secret key. Never persisted in clear; never returned again.
 */
export const WidgetInstanceSecretSchema = object({
  id: string().min(1),
  publicKey: string().min(1),
  /** Plaintext secret key (`sk_*`) — shown once. */
  secretKey: string().min(1),
});
export type WidgetInstanceSecret = z.infer<typeof WidgetInstanceSecretSchema>;
